import React, { useEffect, useLayoutEffect } from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import {
  maybePlayAudioOnce,
  useMaybePlayAudioOnceOnMount,
} from 'features/app-takeover/utils/play-audio-once';
import { ProductCustomization } from 'features/menu/components/product-detail/product-customization';
import { CloseConfirmationDialog } from 'features/menu/components/product-detail/product-detail-flow/dialogs/close-confirmation-dialog';
import { UnsavedChangesDialog } from 'features/menu/components/product-detail/product-detail-flow/dialogs/unsaved-changes-dialog';
import { ProductHome } from 'features/menu/components/product-detail/product-home';
import {
  ProductDetailFlowContent,
  getBackgroundColorByStep,
  productDetailFlowContainerStyles,
} from 'features/menu/components/product-detail/styled';
import { useProductDetailRouteRedirect } from 'features/menu/components/product-detail/use-product-detail-route-redirect';
import { isCustomization } from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { StyleSheet } from 'react-native';
import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import { Box } from 'components/ui';

import { OneMenuDiagnosticPdpContributor } from './diagnostic-pdp-contributor';

const FADE_IN_ANIMATION_DURATION = 700;
const SLIDE_IN_ANIMATION_DURATION = 300;

export const ProductDetail = () => {
  const { currentFlowStep, id } = useProductWizardFlow();
  const { flowDirection } = useProductWizardContext();
  const { backgroundColor: takeoverBg, audio, additionalAudioSteps } = useAppTakeoverPdp(id);

  useMaybePlayAudioOnceOnMount(audio);

  useEffect(() => {
    maybePlayAudioOnce(additionalAudioSteps?.[currentFlowStep.step]);
  }, [additionalAudioSteps, currentFlowStep.step]);

  const isMountedRef = React.useRef<boolean>(false);

  const Animation = flowDirection === 'backward' ? SlideInLeft : SlideInRight;

  const productDetailFlowKey = `${currentFlowStep.step}-${currentFlowStep.slotKey}-${flowDirection}`;

  // LayoutAnimationConfig skipEntering alternative logic for web devices
  useLayoutEffect(() => {
    isMountedRef.current = true;
  });

  return (
    <Box testID="product-detail" style={styles.productDetail}>
      <OneMenuDiagnosticPdpContributor />
      <LayoutAnimationConfig skipEntering>
        <Animated.View
          key={productDetailFlowKey}
          style={productDetailFlowContainerStyles}
          entering={FadeIn.duration(FADE_IN_ANIMATION_DURATION)}
        >
          <Animated.View
            entering={
              isMountedRef.current ? Animation.duration(SLIDE_IN_ANIMATION_DURATION) : undefined
            }
            style={{
              ...productDetailFlowContainerStyles,
              backgroundColor: takeoverBg ?? getBackgroundColorByStep(currentFlowStep.step),
            }}
          >
            <ProductDetailFlowContent testID="product-detail-flow-content">
              <ProductDetailStep />
            </ProductDetailFlowContent>
          </Animated.View>
        </Animated.View>
      </LayoutAnimationConfig>

      <CloseConfirmationDialog />
      <UnsavedChangesDialog />
    </Box>
  );
};

const ProductDetailStep = () => {
  const { currentFlowStep } = useProductWizardFlow();
  const shouldRedirectFromInvalidRoute = useProductDetailRouteRedirect();

  // Redirect runs after render — block wizard steps so invalid URL params cannot
  // mount Product Home / Customization and crash before navigation completes.
  if (shouldRedirectFromInvalidRoute) {
    return null;
  }

  if (isCustomization(currentFlowStep.step)) {
    return <ProductCustomization />;
  }

  return <ProductHome />;
};

const styles = StyleSheet.create({
  productDetail: {
    flex: 1,
  },
});
